import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../Config";

const formatDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).split("T")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const ProformaView = ({ proformaid: proformaidProp }) => {
  const navigate = useNavigate();
  const { proformaid: proformaidParam } = useParams();
  const proformaid = proformaidProp || proformaidParam;

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!proformaid) {
      setIsLoading(false);
      return;
    }

    const fetchProforma = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_URL}/billing/proforma/${proformaid}`);
        const result = await res.json();

        if (!res.ok || !result.success) {
          setLoadError(result.message || "Failed to load proforma invoice.");
          return;
        }

        const inv = result.invoice || result.proforma || result.data;
        if (!inv) {
          setLoadError("Proforma invoice data not found.");
          return;
        }

        const savedExcludeGst =
          inv.excludeGst ?? inv.excludeGST ?? inv.exclude_gst;

        const hasSavedExcludeGst =
          savedExcludeGst !== undefined && savedExcludeGst !== null;

        const isExcludeGst = hasSavedExcludeGst
          ? savedExcludeGst === true ||
            savedExcludeGst === 1 ||
            savedExcludeGst === "1" ||
            String(savedExcludeGst).toLowerCase() === "true"
          : Number(inv.gst || 0) === 0 &&
            (inv.items || []).every(
              (item) =>
                Number(item.cgst || 0) === 0 && Number(item.sgst || 0) === 0,
            );

        setData({
          invoiceno: inv.invoiceno,
          invoicedate: inv.invoicedate,
          guestname: inv.guestname,
          guestaddress: inv.guestaddress || inv.address || "",
          guestgstin: inv.guestgstin || inv.gstin || "",
          excludeGst: isExcludeGst,
          rows: (inv.items || []).map((item) => ({
            particulars: item.particulars,
            from: item.checkin,
            to: item.checkout,
            nights: item.nights,
            rate: item.rate,
            cgst: item.cgst,
            sgst: item.sgst,
          })),
          inWords: inv.amountinwords,
          subTotal: inv.subtotal,
          gst: inv.gst,
          total: inv.total,
          advanceReceived: inv.advanceReceived,
          balanceAmount: inv.balanceAmount,
          securityDeposit: inv.securityDeposit,
        });
      } catch (err) {
        console.error("Error loading proforma invoice:", err);
        setLoadError(
          "Something went wrong while loading the proforma invoice.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProforma();
  }, [proformaid]);

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
        Loading proforma invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
        No proforma invoice to display.
      </div>
    );
  }

  const {
    invoiceno,
    invoicedate,
    guestname,
    guestaddress,
    guestgstin,
    excludeGst,
    rows,
    inWords,
    subTotal,
    gst,
    total,
    advanceReceived,
    balanceAmount,
    securityDeposit,
  } = data;

  return (
    <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
      <style>{`
        @media print { @page { margin: 0; } body { margin: 1.6cm; } .no-print { display: none; } }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="montserrat text-5xl font-extrabold tracking-tight leading-none mb-1 text-[#111]">
            PROFORMA
          </h1>
          <h1 className="montserrat text-5xl font-extrabold tracking-tight mb-2 text-[#111]">
            INVOICE
          </h1>
        </div>
        <div className="w-20 h-20 rounded-full border border-[#111] flex items-center justify-center p-1 bg-[#111]">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain rounded-full"
          />
        </div>
      </div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mb-6">
        <div>
          <p className="montserrat text-[10px] font-bold uppercase tracking-wider text-[#555]">
            INVOICE NO.
          </p>
          <p className="montserrat font-semibold text-sm text-[#111]">
            {invoiceno}
          </p>
        </div>
        <div>
          <p className="montserrat text-[10px] font-bold uppercase tracking-wider text-[#555]">
            DATE
          </p>
          <p className="montserrat font-semibold text-sm text-[#111]">
            {formatDate(invoicedate)}
          </p>
        </div>
      </div>

      <div className="border-t border-[#111] mb-6"></div>

      {/* Bill To & From Section */}
      <div className="flex justify-between items-start mb-8 text-sm">
        <div className="w-1/2 flex flex-col gap-1">
          <p className="montserrat text-[10px] font-bold uppercase tracking-wider text-[#555]">
            BILL TO
          </p>
          <p className="montserrat font-bold text-base text-[#111]">
            {guestname}
          </p>
          {guestgstin && (
            <p className="montserrat text-xs text-[#444]">
              <span className="font-medium">GSTIN NO. :</span>{" "}
              <span>{guestgstin}</span>
            </p>
          )}
          {guestaddress && (
            <p className="montserrat text-xs text-[#444]">{guestaddress}</p>
          )}
        </div>

        <div className="w-1/2 text-right flex flex-col gap-1">
          <p className="montserrat text-[10px] uppercase tracking-wider text-[#555]">
            FROM
          </p>
          <p className="montserrat font-bold text-base text-[#111]">
            Zorba Stays
          </p>
          <p className="montserrat text-xs text-[#444]">
            GSTIN NO.: 24AADFZ2976K1ZO
          </p>
          <p className="montserrat text-xs text-[#444] leading-relaxed">
            306-7 - Business Centrum,
            <br />
            Amin Marg,
            <br />
            Rajkot, 360001
            <br />
            India
          </p>
        </div>
      </div>

      {/* Table (Read Only) */}
      <table className="w-full border-collapse border-t border-b border-[#111] text-center text-xs">
        <thead>
          <tr className="montserrat uppercase font-bold border-b border-[#111] text-[11px] text-[#333]">
            <th className="py-3 w-[50px] text-left pl-2">SR. NO.</th>
            <th className="py-3 text-left">PARTICULARS</th>
            <th className="py-3">PERIOD OF STAY</th>
            <th className="py-3">NO. OF NIGHTS</th>
            <th className="py-3">PER NIGHT RATE</th>
            {!excludeGst && <th className="py-3">CGST</th>}
            {!excludeGst && <th className="py-3">SGST</th>}
          </tr>
        </thead>
        <tbody>
          {rows?.map((row, index) => (
            <tr
              key={index}
              className="montserrat border-b border-[#111] text-xs"
            >
              <td className="py-3 text-left pl-2 font-medium">{index + 1}</td>
              <td className="py-3 text-left text-[#111]">{row.particulars}</td>
              <td className="py-3 text-[#111]">
                {formatDate(row.from)} to {formatDate(row.to)}
              </td>
              <td className="py-3 text-[#111]">{row.nights}</td>
              <td className="py-3 text-[#111]">{row.rate}</td>
              {!excludeGst && <td className="py-3 text-[#111]">{row.cgst}</td>}
              {!excludeGst && <td className="py-3 text-[#111]">{row.sgst}</td>}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals Section */}
      <div className="w-full mt-6 px-2 text-sm text-[#111]">
        {/* Sub Total */}
        {!excludeGst && (
          <div className="montserrat flex justify-end items-center py-1.5 font-medium">
            <span className="w-40 text-left text-[#555]">Amount Due</span>
            <span className="w-32 text-right">{subTotal}</span>
          </div>
        )}

        {/* GST */}
        {!excludeGst && (
          <div className="montserrat flex justify-end items-center py-1.5 font-medium">
            <span className="w-40 text-left text-[#555]">GST(18%)</span>
            <span className="w-32 text-right">{gst}</span>
          </div>
        )}

        {/* Total */}
        <div className="montserrat flex justify-end items-center py-3 border-t border-b border-[#263238] font-bold">
          <span className="w-40 text-left">Total</span>
          <span className="w-32 text-right">{total}</span>
        </div>

        {/* (Advance, Balance, Security Deposit) */}
        <div className="mt-4 flex flex-col gap-1.5">
          <div className="montserrat flex justify-between items-center py-1 font-medium">
            <span className="text-left text-[#555]">
              Advance to be Received
            </span>
            <span className="w-32 text-right">{advanceReceived || "0.00"}</span>
          </div>

          <div className="montserrat flex justify-between items-center py-1 font-medium">
            <span className="text-left text-[#555]">Balance amount</span>
            <span className="w-32 text-right">{balanceAmount || "0.00"}</span>
          </div>

          <div className="montserrat flex justify-between items-center py-1 font-medium border-b border-[#263238] pb-3">
            <span className="text-left text-[#555]">Security Deposit</span>
            <span className="w-32 text-right">{securityDeposit || "0.00"}</span>
          </div>
        </div>

        {/* Amount In Words */}
        <div className="mt-6">
          <p className="montserrat text-[10px] font-medium uppercase tracking-wide text-[#555] mb-1">
            IN WORDS
          </p>
          <p className="montserrat w-full text-sm font-extrabold uppercase">
            {inWords}
          </p>
        </div>
      </div>

      {/* Thank you & Policies / Payment Info */}
      <div className="montserrat mt-10 text-sm font-extrabold">
        <p>Thank you!</p>
        <p>It's been a pleasure hosting you!</p>
      </div>

      <div className="flex justify-between items-start mt-8 text-xs text-[#333] gap-8">
        <div className="w-1/2 flex flex-col gap-2">
          <p className="montserrat font-bold text-[10px] uppercase text-[#555]">
            CANCELLATION POLICY
          </p>
          <ul className="list-disc pl-4 space-y-1">
            <li>
              30 days prior to the check-in date, 100% payment refundable.
            </li>
            <li>
              If cancellation is not made before the 30 days then advance amount
              will be forfeited.
            </li>
          </ul>
        </div>

        <div className="w-1/2 flex flex-col gap-2">
          <p className="montserrat font-bold text-[10px] uppercase text-[#555]">
            PAYMENT INFO
          </p>
          <p className="leading-relaxed text-[#444]">
            HDFC Bank
            <br />
            Ac NO.: 50200077506738
            <br />
            Ac Name: ZORB A STAYS
            <br />
            IFSC: HDFC0000101
            <br />
            Branch: RAJKOT - YAGNIK ROAD - GUJARAT
            <br />
            Account Type: CURRENT
          </p>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-20 h-20 border border-gray-300 p-1 bg-white">
              <img
                src="/"
                alt="QR Code"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
            <div className="text-[11px] mt-12">
              <p className="font-semibold text-[#111]">Name: ZORBA STAYS</p>
              <p className="text-[#555]">UPI ID: 8866262368@hdfcbank</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="mt-12 flex flex-col gap-2">
        <div className="flex items-center px-0">
          <div className="flex-1 border-t border-[#111]"></div>
          <div className="px-3 flex items-center justify-center">
            <img
              src="../assets/zorba_logo.webp"
              alt="Zorba Logo"
              className="w-7 h-7 object-contain"
            />
          </div>
          <div className="flex-1 border-t border-[#111]"></div>
        </div>

        <div className="montserrat flex justify-between items-center text-xs text-[#555] px-1">
          <span>@zorba_stays</span>
          <span>www.zorbastays.com</span>
        </div>
      </div>

      {/* Print Button */}
      <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
        <button
          onClick={() => window.print()}
          className="px-6 py-2 bg-green-900 rounded-lg text-center text-white text-sm font-semibold hover:bg-green-800 transition-all"
        >
          Print Proforma
        </button>
        <button
          onClick={() => navigate("/bills")}
          className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-all text-sm font-semibold"
        >
          Back to Bills
        </button>
      </div>
    </div>
  );
};

export default ProformaView;
